/**
 * AURELIA — component-product-form.js
 * <product-form> — intercepts an add-to-cart submission and performs it over
 * AJAX, keeping the customer on the page.
 *
 * The <form> is a real Shopify product form with a real action, so with
 * JavaScript disabled the browser submits it normally and the customer still
 * reaches the cart. Nothing here is required for a purchase to complete.
 */
(function () {
  'use strict';

  const { Element, cart, a11y, config, emit } = window.Aurelia;

  class ProductForm extends Element {
    mount() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.submitButton = this.form.querySelector('[type="submit"]');
      this.errorTarget = this.querySelector('[data-form-error]');
      this.isQuickAdd = this.hasAttribute('data-quick-add');

      this.listen(this.form, 'submit', (event) => this.handleSubmit(event));

      // A variant that becomes unavailable must disable the button even if the
      // customer never touches the form again.
      this.subscribe('variant:change', (detail) => {
        if (detail.sectionId && detail.sectionId !== this.dataset.sectionId) return;
        this.syncAvailability(detail);
      });
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (this.busy) return;

      this.clearError();
      this.setBusy(true);

      const formData = new FormData(this.form);
      const item = {
        id: Number(formData.get('id')),
        quantity: Number(formData.get('quantity')) || 1,
      };

      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) item.selling_plan = Number(sellingPlan);

      // Line-item properties arrive as properties[Name]; collect them into the
      // shape the Cart API expects.
      const properties = {};
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && String(value).trim() !== '') properties[match[1]] = value;
      });
      if (Object.keys(properties).length > 0) item.properties = properties;

      try {
        await cart.add(item, { source: this.isQuickAdd ? 'quick-add' : 'product-form' });
        // The button is named explicitly because setBusy() disabled it for the
        // duration of the request, which blurred it — by now the drawer cannot
        // work out what to hand focus back to by looking at the document.
        emit('cart:open-request', { reason: 'add', opener: this.submitButton });
      } catch (error) {
        this.showError(error.message || config.strings.cartError);
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(isBusy) {
      this.busy = isBusy;
      if (!this.submitButton) return;
      this.submitButton.setAttribute('aria-busy', String(isBusy));
      this.submitButton.disabled = isBusy;
    }

    showError(message) {
      a11y.announce(message, true);

      if (!this.errorTarget) {
        // Without a dedicated slot the message still has to reach the customer.
        this.errorTarget = document.createElement('div');
        this.errorTarget.className = 'form-message form-message--error';
        this.errorTarget.setAttribute('data-form-error', '');
        this.form.appendChild(this.errorTarget);
      }

      this.errorTarget.textContent = message;
      this.errorTarget.hidden = false;
    }

    clearError() {
      if (!this.errorTarget) return;
      this.errorTarget.textContent = '';
      this.errorTarget.hidden = true;
    }

    syncAvailability({ variant }) {
      if (!this.submitButton) return;

      const label = this.submitButton.querySelector('.button__label');
      const idInput = this.form.querySelector('input[name="id"]');
      if (idInput && variant) idInput.value = variant.id;

      if (!variant) {
        this.submitButton.disabled = true;
        if (label) label.textContent = config.strings.unavailable;
        return;
      }

      this.submitButton.disabled = !variant.available;
      if (label) {
        label.textContent = variant.available ? config.strings.addToCart : config.strings.soldOut;
      }
    }
  }

  window.Aurelia.define('product-form', ProductForm);
})();
